/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { find } from "lodash";
import { produce } from "immer";
import PropTypes from "prop-types";
import V2Button from "@shared/v2/Button";
import TextButton from "@shared/v2/TextButton";
import { ChevronLeftSolidV6 } from "@shared/v2/Icomoon";
import Dropdown from "@shared/v2/Dropdown";
import RadioButton from "@shared/v2/RadioButton";
import { CheckboxOption } from "@shared/v2/Dropdown/custom/MultiComponents";
import { FormGroup, Row, Col, Button } from "../../shared/v1";
import AutoPlanNameForm from "./AutoPlanNameForm";
import Actions from "./Actions";
import SpecificDates from "./SpecificDates";
import AutoPauseOnCondition from "./AutoPauseOnCondition";
import AutoPauseStatusPill from "./AutoPauseStatusPill";
import TaskActionModal from "./TaskActionModal";
import {
  ANY_STATUS_VALUE,
  parseAutoPauseStatuses,
  serializeAutoPauseStatuses,
  peopleAutoPauseStatusOptions,
  peopleCompletionStatusOptions,
  transactionAutoPauseStatusOptions,
  transactionCompletionStatusOptions,
  // TODO Work on this
  // validateTaskForm
} from "./helpers";
import "./AutoPlan.scss";
import {
  useIsTaskActionModalOpen,
  useIsEmailActionModalOpen,
  useIsTextActionModalOpen,
  useActionForm,
  useIsSubmitting,
} from "../../reducers/autoPlanEdit";
import {
  toggleTaskActionModal,
  toggleEmailActionModal,
  toggleTextActionModal,
  setPeopleGroups,
} from "../actions/autoPlansActionCreators";
import EmailActionModal from "./EmailActionModal";
import TextActionModal from "./TextActionModal";
import { useActionFormSubmitHandler } from "../hooks";
import { checkIfDateStringIsWithinRange } from "../helpers";
import { submitActionFailure } from "../actions/autoPlanActionCreators";

const TEXT_EXPIRED = {
  people: "Auto pause plan when lead replies via text?",
  transactions: "Auto pause when transaction expires.",
};
const STATUS_CHANGE = {
  people: "Auto pause plan when lead's status changes?",
  transactions: "Auto pause when transaction's status changes?",
};

const STATUS_CHANGE_TO = {
  people: "Pause plan when lead's status changes to",
  transactions: "Pause plan when transaction's status changes to",
};

const AUTO_PAUSE_ON_COMPLETE = {
  people: "Auto change lead's status when the plan is completed to:",
  transactions: "Auto change transaction's status when the plan is completed to:",
};

const autoPauseReplyOrExpireNames = {
  people: "auto_pause_on_reply",
  transactions: "auto_pause_on_expired",
};

const filterGroupOptionsByPlanTypes = (groups, planTypes) =>
  groups.options.map(({ label, options }) => ({
    label,
    options: options.filter((op) => planTypes.includes(op.forType)),
  }));

const AutoPlanEdit = ({
  assignedForOptions,
  assignedToOptions,
  autoPlanId,
  autoPlanPath,
  autoPlan,
  autoPlanForm,
  autoPlanPreferencesForm,
  emailTemplates,
  getAutoPlan,
  handleAutoPlanChange,
  isEditingName,
  isEditingPreferences,
  isFetching,
  milestones,
  peoplePlaceholders,
  resetAutoPlanPreferencesForm,
  saveAutoPlanName,
  saveAutoPlanPreferences,
  toggleEdit,
  transactionPlaceholders,
  updateAutoPlanPreferencesForm,
}) => {
  useEffect(() => {
    getAutoPlan(autoPlanPath);
  }, [autoPlanPath, getAutoPlan]);

  const completionStatusOptions =
    autoPlan.plan_type === "people" ? peopleCompletionStatusOptions : transactionCompletionStatusOptions;

  const autoPauseStatusOptions =
    autoPlan.plan_type === "people" ? peopleAutoPauseStatusOptions : transactionAutoPauseStatusOptions;

  const planType = autoPlan.plan_type;
  const validPlanTypes = ["all", planType];
  const filteredAssignedToOptions = filterGroupOptionsByPlanTypes(assignedToOptions, validPlanTypes);
  const filteredAssignedForOptions = filterGroupOptionsByPlanTypes(assignedForOptions, validPlanTypes);

  const filteredMilestoneOptions = milestones.filter((op) => op.forPlanType === planType);
  const autoPauseReplyOrExpireName = autoPauseReplyOrExpireNames[planType];

  const placeholders = planType === "people" ? peoplePlaceholders : transactionPlaceholders;
  const dispatch = useDispatch();
  const isEmailActionModalOpen = useIsEmailActionModalOpen();
  const isTextActionModalOpen = useIsTextActionModalOpen();
  const isSubmitting = useIsSubmitting();
  const isTaskActionModalOpen = useIsTaskActionModalOpen();
  const actionForm = useActionForm();
  const actionFormSubmitHandlr = useActionFormSubmitHandler(autoPlanId);

  useEffect(() => {
    const [generalRoles, teamRoles, userId] = assignedToOptions.options;

    dispatch(setPeopleGroups(generalRoles, teamRoles, userId));
  }, []);

  const autoPauseForStatus = autoPlanPreferencesForm?.auto_pause_for_status;
  const selectedStatusValues = useMemo(() => parseAutoPauseStatuses(autoPauseForStatus), [autoPauseForStatus]);

  // Can't be derived from the form: clearing every status doesn't mean the user switched back to "No".
  const [isPauseForStatusEnabled, setIsPauseForStatusEnabled] = useState(false);

  // "No" writes the form empty, as that's how the service is told not to pause. This keeps the picks.
  const [retainedStatusValues, setRetainedStatusValues] = useState([]);

  // Keyed on the form, not `autoPlan`: Cancel restores the form *from* `autoPlan` without changing it.
  useEffect(() => {
    if (isEditingPreferences) return;

    const formStatusValues = parseAutoPauseStatuses(autoPauseForStatus);

    setIsPauseForStatusEnabled(formStatusValues.length > 0);
    setRetainedStatusValues(formStatusValues);
  }, [autoPauseForStatus, isEditingPreferences]);

  const displayedStatusValues = isPauseForStatusEnabled ? selectedStatusValues : retainedStatusValues;
  const displayedStatusOptions = useMemo(
    () => autoPauseStatusOptions.filter((option) => displayedStatusValues.includes(option.value)),
    [autoPauseStatusOptions, displayedStatusValues],
  );

  const writeStatuses = useCallback(
    (values) => updateAutoPlanPreferencesForm("auto_pause_for_status", serializeAutoPauseStatuses(values)),
    [updateAutoPlanPreferencesForm],
  );

  const setSelectedStatuses = useCallback(
    (values) => {
      setRetainedStatusValues(values);
      writeStatuses(values);
    },
    [writeStatuses],
  );

  // "Any Status" is mutually exclusive: picking it drops the rest, and picking anything else drops it.
  const handleStatusChange = useCallback(
    (options) => {
      const values = (options || []).map((option) => option.value);
      const isAddingAny =
        values.includes(ANY_STATUS_VALUE) && !selectedStatusValues.includes(ANY_STATUS_VALUE);

      setSelectedStatuses(
        isAddingAny ? [ANY_STATUS_VALUE] : values.filter((value) => value !== ANY_STATUS_VALUE),
      );
    },
    [selectedStatusValues, setSelectedStatuses],
  );

  const handleRemoveStatus = useCallback(
    (value) => setSelectedStatuses(selectedStatusValues.filter((selected) => selected !== value)),
    [selectedStatusValues, setSelectedStatuses],
  );

  const enablePauseForStatus = useCallback(() => {
    if (isPauseForStatusEnabled) return;

    setIsPauseForStatusEnabled(true);
    // Puts the retained picks back, so "No" then "Yes" restores rather than starting over.
    if (retainedStatusValues.length > 0) writeStatuses(retainedStatusValues);
  }, [isPauseForStatusEnabled, retainedStatusValues, writeStatuses]);

  const disablePauseForStatus = useCallback(() => {
    setIsPauseForStatusEnabled(false);

    // Only the form clears; the pills stay. Skipped when empty so "No" on "No" can't dirty the form.
    if (selectedStatusValues.length > 0) writeStatuses([]);
  }, [selectedStatusValues, writeStatuses]);

  let statusSummaryLabel = "Select";
  if (displayedStatusOptions.length === 1) {
    statusSummaryLabel = displayedStatusOptions[0].label;
  } else if (displayedStatusOptions.length > 1) {
    statusSummaryLabel = `(${displayedStatusOptions.length}) Selected`;
  }

  return (
    <>
      <TaskActionModal
        assignedToOptions={filteredAssignedToOptions}
        show={isTaskActionModalOpen}
        autoPlanId={autoPlanId}
        onHide={() => dispatch(toggleTaskActionModal(false))}
        form={actionForm}
        isSubmitting={isSubmitting}
        onSave={(form) => {
          const yearErrorMsg = checkIfDateStringIsWithinRange(form.day_month_year);
          if (yearErrorMsg) {
            dispatch(submitActionFailure(yearErrorMsg));
            return;
          }
          actionFormSubmitHandlr(form);
        }}
      />
      <EmailActionModal
        show={isEmailActionModalOpen}
        emailTemplates={emailTemplates}
        autoPlanId={autoPlanId}
        assignedToOptions={filteredAssignedToOptions}
        isSubmitting={isSubmitting}
        onHide={() => dispatch(toggleEmailActionModal(false))}
        form={actionForm}
        placeholders={placeholders}
        onSave={(form) => {
          const yearErrorMsg = checkIfDateStringIsWithinRange(form.day_month_year);

          if (yearErrorMsg) {
            dispatch(submitActionFailure(yearErrorMsg));
            return;
          }
          const adaptedForm = produce(form, (draft) => {
            draft.bcc_emails = draft.bcc_emails.map((el) => el.value);
            draft.bcc_people_uuids = draft.bcc_people_uuids.map((el) => el.value);
            draft.bcc_role_uuids = draft.bcc_role_uuids.map((el) => el.value);
            draft.cc_emails = draft.cc_emails.map((el) => el.value);
            draft.cc_people_uuids = draft.cc_people_uuids.map((el) => el.value);
            draft.cc_role_uuids = draft.cc_role_uuids.map((el) => el.value);
          });

          actionFormSubmitHandlr(adaptedForm);
        }}
      />
      <TextActionModal
        show={isTextActionModalOpen}
        onHide={() => dispatch(toggleTextActionModal(false))}
        placeholders={placeholders}
        isSubmitting={isSubmitting}
        form={actionForm}
        assignedToOptions={filteredAssignedToOptions}
        autoPlanId={autoPlanId}
        onSave={(form) => {
          const yearErrorMsg = checkIfDateStringIsWithinRange(form.day_month_year);

          if (yearErrorMsg) {
            dispatch(submitActionFailure(yearErrorMsg));
            return;
          }
          actionFormSubmitHandlr(form);
        }}
      />
      <div className="tw-bg-tinted-gray-50 tw-min-h-[100vh]">
        <div className="tw-bg-[#fff]">
          <Row className="tw-max-w-[1200px] tw-mx-auto tw-py-[24px]">
            <Col size={4}>
              <section>
                <V2Button schema="tertiary" className="tw-p-8px tw-color-gray-base">
                  <a href={`/auto_plans?plan_type=${autoPlan.plan_type}`} className="hover:tw-no-underline">
                    <ChevronLeftSolidV6 size="s" className="tw-mr-[4px]" />
                    <span>Back</span>
                  </a>
                </V2Button>
                <Row>
                  {autoPlan && (
                    <AutoPlanNameForm
                      handleNameChange={handleAutoPlanChange}
                      isEditingName={isEditingName}
                      toggleEdit={toggleEdit}
                      updateName={() => saveAutoPlanName(autoPlanPath, autoPlanForm)}
                      name={autoPlanForm.name}
                    />
                  )}
                </Row>
                <Row className="tw-mt-[26px]">
                  <Col size={12}>
                    <label className="tw-text-12d tw-text-gray-50 tw-mb-[8px]">Type:</label>
                    {autoPlan && (
                      <div className="action-plan-type-header tw-capitalize tw-text-14d">
                        {autoPlan.plan_type} Plan
                      </div>
                    )}
                  </Col>
                </Row>
              </section>
            </Col>
            <Col size={4}>
              <section>
                <Row>
                  <label className="tw-mb-[12px]">{TEXT_EXPIRED[autoPlan.plan_type]}</label>
                  {autoPauseReplyOrExpireName && (
                    <AutoPauseOnCondition
                      name={autoPauseReplyOrExpireName}
                      checkedValue={
                        autoPlanPreferencesForm && autoPlanPreferencesForm[autoPauseReplyOrExpireName]
                      }
                      handleUpdate={updateAutoPlanPreferencesForm}
                      disabled={isFetching}
                    />
                  )}
                </Row>
                <Row>
                  <Dropdown
                    label={AUTO_PAUSE_ON_COMPLETE[autoPlan.plan_type]}
                    labelClassName="!tw-text-12d !tw-font-normal tw-mb-[12px]"
                    name="status_on_complete"
                    className="tw-w-[174px] tw-cursor-pointer"
                    value={find(completionStatusOptions, {
                      value: autoPlanPreferencesForm?.status_on_complete,
                    })}
                    options={completionStatusOptions}
                    isClearable={false}
                    isSearchable={false}
                    isDisabled={isFetching}
                    onChange={(opt) => updateAutoPlanPreferencesForm("status_on_complete", opt.value)}
                  />
                </Row>
              </section>
            </Col>
            <Col size={4}>
              <Row>
                <label className="tw-mb-[12px]">{STATUS_CHANGE[autoPlan.plan_type]}</label>
                <FormGroup>
                  <RadioButton
                    id="auto-pause-for-status-yes"
                    value="yes"
                    name="auto_pause_for_status_enabled"
                    checked={isPauseForStatusEnabled}
                    onClick={enablePauseForStatus}
                    disabled={isFetching}
                    labelClassName="tw-text-gray-base tw-text-[14px]"
                    containerClassName="tw-mr-[12px] tw-cursor-pointer"
                    label="Yes"
                  />
                  <RadioButton
                    id="auto-pause-for-status-no"
                    value="no"
                    name="auto_pause_for_status_enabled"
                    checked={!isPauseForStatusEnabled}
                    onClick={disablePauseForStatus}
                    disabled={isFetching}
                    labelClassName="tw-text-gray-base tw-text-[14px]"
                    containerClassName="tw-cursor-pointer"
                    label="No"
                  />
                  {/* Full width is the design's 400px (Col size={4} of a 1200px Row) and tracks the grid. */}
                  <div className="tw-mt-[12px] tw-w-full">
                    <Dropdown
                      isMulti
                      label={STATUS_CHANGE_TO[autoPlan.plan_type]}
                      labelClassName="!tw-text-12d !tw-font-normal tw-mb-[12px]"
                      name="auto_pause_for_status"
                      className="tw-w-full tw-cursor-pointer"
                      placeholder={statusSummaryLabel}
                      value={displayedStatusOptions}
                      options={autoPauseStatusOptions}
                      components={{ Option: CheckboxOption }}
                      // Control shows "(2) Selected"; we render the pills below so they can carry colors.
                      controlShouldRenderValue={false}
                      multiShouldRenderBelowSelect={false}
                      hideSelectedOptions={false}
                      closeMenuOnSelect={false}
                      blurInputOnSelect={false}
                      isClearable={false}
                      isSearchable={false}
                      isDisabled={isFetching || !isPauseForStatusEnabled}
                      onChange={handleStatusChange}
                    />
                    {/* Kept mounted under "No" so the greyed-out picks stay visible; form value is empty. */}
                    {displayedStatusOptions.length > 0 && (
                      <div className="tw-mt-[8px] tw-flex tw-flex-wrap tw-items-center tw-gap-[8px]">
                        {displayedStatusOptions.map((option) => (
                          <AutoPauseStatusPill
                            key={option.value}
                            planType={planType}
                            option={option}
                            isDisabled={!isPauseForStatusEnabled}
                            onRemove={handleRemoveStatus}
                          />
                        ))}
                        {/* "!" on both: the enabled color would beat the schema's disabled variant. */}
                        <TextButton
                          className={`tw-p-0 !tw-text-12d ${
                            isPauseForStatusEnabled ? "!tw-text-gray-75" : "!tw-text-neutral-gray-30"
                          }`}
                          schema="sentence"
                          disabled={!isPauseForStatusEnabled}
                          onClick={() => setSelectedStatuses([])}
                        >
                          Clear all
                        </TextButton>
                      </div>
                    )}
                  </div>
                </FormGroup>
              </Row>
              <Row>
                {isEditingPreferences && (
                  <div>
                    <Button
                      variant="info"
                      onClick={() => saveAutoPlanPreferences(autoPlanPath, autoPlanPreferencesForm)}
                      className="m-r-10"
                    >
                      Save
                    </Button>
                    <Button variant="info" invert onClick={resetAutoPlanPreferencesForm}>
                      Cancel
                    </Button>
                  </div>
                )}
              </Row>
            </Col>
          </Row>
        </div>
        <div>
          <Row
            className="pull-content-to-side-menu tw-max-w-[1200px] tw-mx-auto"
            deprecatedOverrideMarginClasses="tw--mx-20px tw-mt-[24px]"
          >
            <section>
              <div className="brivity-panel tw-border tw-border-solid tw-border-gray-10 tw-rounded-[8px]">
                <div className="!tw-text-gray-50 !tw-text-18d !tw-px-[16px] !tw-py-[12px] tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-10">
                  Actions
                </div>
                <div className="tw-p-[32px]">
                  <SpecificDates />
                  <Row className="tw-mt-[32px]">
                    <Col size={12}>
                      {autoPlan && (
                        <Actions
                          planType={autoPlan.type}
                          actions={autoPlan.actions}
                          autoPlanStartingTime={autoPlan.starting_time}
                          milestoneOptions={filteredMilestoneOptions}
                          assignedToOptions={filteredAssignedToOptions}
                          assignedForOptions={filteredAssignedForOptions}
                          autoPlanPath={autoPlanPath}
                          placeholders={placeholders}
                        />
                      )}
                    </Col>
                  </Row>
                </div>
              </div>
            </section>
          </Row>
        </div>
      </div>
    </>
  );
};

AutoPlanEdit.propTypes = {
  assignedForOptions: PropTypes.shape().isRequired,
  assignedToOptions: PropTypes.shape().isRequired,
  autoPlanId: PropTypes.string.isRequired,
  autoPlanPath: PropTypes.string.isRequired,
  autoPlan: PropTypes.objectOf(PropTypes.any).isRequired,
  autoPlanForm: PropTypes.objectOf(PropTypes.any).isRequired,
  autoPlanPreferencesForm: PropTypes.objectOf(PropTypes.any).isRequired,
  emailTemplates: PropTypes.arrayOf(PropTypes.any).isRequired,
  getAutoPlan: PropTypes.func.isRequired,
  handleAutoPlanChange: PropTypes.func.isRequired,
  isEditingName: PropTypes.bool.isRequired,
  isEditingPreferences: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  milestones: PropTypes.arrayOf(PropTypes.any).isRequired,
  peoplePlaceholders: PropTypes.arrayOf(PropTypes.any).isRequired,
  resetAutoPlanPreferencesForm: PropTypes.func.isRequired,
  saveAutoPlanName: PropTypes.func.isRequired,
  saveAutoPlanPreferences: PropTypes.func.isRequired,
  toggleEdit: PropTypes.func.isRequired,
  transactionPlaceholders: PropTypes.arrayOf(PropTypes.any).isRequired,
  updateAutoPlanPreferencesForm: PropTypes.func.isRequired,
};

export default AutoPlanEdit;
